<template>
    <div class="cancelled-order-show order-detail-v1 dlm-admin-theme" data-testid="cancelled-order-island">
        <div class="alert alert-warning order-detail-v1__banner" role="status">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            {{ label('preview_banner', 'Ordine in annullati — BeeStore resta cancellato. Riattiva copia solo la testata DLM.') }}
        </div>

        <div class="order-detail-v1__header-stripe">
            <div class="row">
                <div class="col-md-6">
                    <div class="order-detail-v1__header-lead">
                        <h1 class="order-detail-v1__header-title">
                            {{ header.canceled_num }}
                            <span class="label label-portal text-uppercase">{{ portalLabel }}</span>
                            <span class="label label-archive"><i class="fa fa-ban"></i> Annullato</span>
                        </h1>
                        <div class="order-detail-v1__header-buttons">
                            <a class="btn btn-show-back" :href="payload.routes.index">
                                <i class="fa fa-arrow-left" aria-hidden="true"></i> {{ label('back', 'Indietro') }}
                            </a>
                            <button
                                type="button"
                                class="btn btn-primary"
                                data-testid="cancelled-order-reactivate"
                                :disabled="reactivateDisabled"
                                @click="onReactivateClick"
                            >
                                <i class="fa fa-rotate-left"></i>
                                {{ reactivating ? '…' : label('reactivate', 'Riattiva') }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="col-md-6">
                    <dl class="order-detail-v1__header-meta row">
                        <div class="col-sm-6 col-md-3">
                            <dt>Entity</dt>
                            <dd>{{ header.entity_id ?? '—' }}</dd>
                        </div>
                        <div class="col-sm-6 col-md-3">
                            <dt>IGU prenotazione</dt>
                            <dd><code>{{ header.igu_prenotazione || '—' }}</code></dd>
                        </div>
                        <div class="col-sm-6 col-md-3">
                            <dt>Nº prenotazione</dt>
                            <dd>{{ header.canceled_num ?? '—' }}</dd>
                        </div>
                        <div class="col-sm-6 col-md-3">
                            <dt>Prenotato il</dt>
                            <dd>{{ header.data_prenotazione_formatted || header.data_prenotazione || '—' }}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>

        <div class="panel panel-default cancelled-order-show__restore-plan">
            <div class="panel-heading panel-heading--with-badge">
                <h4>{{ label('reactivate_plan', 'Piano di Riattiva') }}</h4>
                <span class="cancelled-order-show__plan-badge" :class="planLabelClass">{{ payload.plan?.status_label }}</span>
            </div>
            <div class="panel-body no-padding">
                <dl class="cancelled-order-show__plan-fields">
                    <div>
                        <dt>Strategia numero</dt>
                        <dd>{{ payload.plan?.number_strategy_label || '—' }}</dd>
                    </div>
                    <div>
                        <dt>Conferma nuovo numero</dt>
                        <dd>{{ payload.plan?.requires_confirm_new_number ? 'Richiesta' : 'No' }}</dd>
                    </div>
                </dl>
                <p v-if="allocateNewHint" class="cancelled-order-show__plan-hint">
                    <i class="fa fa-exclamation-triangle"></i> {{ allocateNewHint }}
                </p>
            </div>
        </div>

        <div class="panel panel-default">
            <div class="panel-heading">
                <h4>{{ label('summary', 'Riepilogo ordine annullato') }}</h4>
            </div>
            <div class="panel-body no-padding">
                <dl class="order-detail-v1-field-grid">
                    <div class="order-detail-v1-field-grid__cell">
                        <dt>Nominativo</dt>
                        <dd>{{ header.nominativo?.trim() || '—' }}</dd>
                    </div>
                    <div class="order-detail-v1-field-grid__cell">
                        <dt>Importo pagato</dt>
                        <dd>{{ summaryAmountPaid }}</dd>
                    </div>
                    <div class="order-detail-v1-field-grid__cell">
                        <dt>Pagamento</dt>
                        <dd>{{ summary.payment_description || header.payment_description || '—' }}</dd>
                    </div>
                    <div class="order-detail-v1-field-grid__cell">
                        <dt>Da fatturare</dt>
                        <dd>{{ invoiceRequestedLabel }}</dd>
                    </div>
                    <div class="order-detail-v1-field-grid__cell">
                        <dt>Note</dt>
                        <dd>{{ summary.note || header.note || '—' }}</dd>
                    </div>
                </dl>
            </div>
        </div>

        <div class="row">
            <div class="col-md-6">
                <div class="panel panel-default">
                    <div class="panel-heading">
                        <h4>{{ label('billing', 'Fatturazione') }}</h4>
                    </div>
                    <div class="panel-body">
                        <address class="cancelled-order-show__address">
                            <strong>{{ billingAddress.name || '—' }}</strong><br>
                            {{ billingAddress.street || '—' }}<br>
                            {{ billingAddress.zip || '' }} {{ billingAddress.city || '—' }}
                            ({{ billingAddress.province || '—' }}) · {{ billingAddress.country_code || '—' }}<br>
                            <span class="text-muted">{{ billingAddress.phone || '—' }}</span>
                        </address>
                        <h5 class="cancelled-order-show__fiscal-title">{{ label('fiscal', 'Dati fiscali') }}</h5>
                        <dl class="order-detail-v1-field-grid">
                            <div class="order-detail-v1-field-grid__cell">
                                <dt>Codice fiscale</dt>
                                <dd>{{ billingAddress.fiscal_code || '—' }}</dd>
                            </div>
                            <div class="order-detail-v1-field-grid__cell">
                                <dt>Partita IVA</dt>
                                <dd>{{ billingAddress.vat_number || '—' }}</dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="panel panel-default">
                    <div class="panel-heading">
                        <h4>{{ label('shipping', 'Spedizione') }}</h4>
                    </div>
                    <div class="panel-body">
                        <address class="cancelled-order-show__address">
                            <strong>{{ shippingAddress.name || '—' }}</strong><br>
                            {{ shippingAddress.street || '—' }}<br>
                            {{ shippingAddress.zip || '' }} {{ shippingAddress.city || '—' }}
                            ({{ shippingAddress.province || '—' }}) · {{ shippingAddress.country_code || '—' }}<br>
                            <span class="text-muted">{{ shippingAddress.phone || '—' }}</span>
                        </address>
                    </div>
                </div>
            </div>
        </div>

        <div class="panel panel-default" data-testid="cancelled-order-lines">
            <div class="panel-heading panel-heading--with-badge">
                <h4>{{ label('lines', 'Righe ordine (ordini_righe)') }}</h4>
                <span class="badge">{{ orderRows.length }}</span>
            </div>
            <div class="panel-body no-padding">
                <p v-if="orderRows.length === 0" class="cancelled-order-show__linked-widget-empty">
                    Nessuna riga
                </p>
                <dl v-else class="cancelled-order-show__plan-fields">
                    <div v-for="row in orderRows" :key="'line-' + row.codice_riga">
                        <dt>{{ row.codice_riga }} · {{ row.cod_articolo || '—' }}</dt>
                        <dd>qty {{ row.quantita ?? '—' }} · {{ row.prz_netto_formatted || '—' }}</dd>
                    </div>
                </dl>
            </div>
        </div>

        <div class="panel panel-default" data-testid="cancelled-order-prenotazioni">
            <div class="panel-heading panel-heading--with-badge">
                <h4>{{ label('prenotazioni', 'Prenotazioni BeeStore (prenotazioni)') }}</h4>
                <span class="badge">{{ prenotazioni.length }}</span>
            </div>
            <div class="panel-body no-padding">
                <p v-if="prenotazioni.length === 0" class="cancelled-order-show__linked-widget-empty">
                    Nessuna prenotazione
                </p>
                <dl v-else class="cancelled-order-show__plan-fields">
                    <div v-for="row in prenotazioni" :key="'pre-' + row.codice_riga">
                        <dt>{{ row.codice_riga }} · {{ row.cod_articolo || '—' }}</dt>
                        <dd>qty {{ row.quantita ?? '—' }} · {{ row.igu_prenotazione || '—' }}</dd>
                    </div>
                </dl>
            </div>
        </div>
    </div>
</template>

<script>
import './island-styles.css';

export default {
    name: 'CancelledOrderIsland',
    props: {
        initialPayload: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            payload: this.initialPayload,
            reactivating: false,
        };
    },
    computed: {
        header() {
            return this.payload.header ?? {};
        },
        summary() {
            return this.payload.summary ?? {};
        },
        billingAddress() {
            return this.payload.billing_address ?? {};
        },
        shippingAddress() {
            return this.payload.shipping_address ?? {};
        },
        orderRows() {
            return this.payload.order_rows ?? [];
        },
        prenotazioni() {
            return this.payload.prenotazioni ?? [];
        },
        summaryAmountPaid() {
            if (this.summary.amount_paid_formatted) {
                return this.summary.amount_paid_formatted;
            }

            return this.amountPaid;
        },
        invoiceRequestedLabel() {
            if (this.billingAddress.invoice_requested || this.header.da_fatturare === '1') {
                return 'Sì';
            }

            return 'No';
        },
        portalLabel() {
            const p = this.header.portale;
            if (!p || String(p).trim() === '') {
                return 'dlm';
            }

            return String(p).toLowerCase();
        },
        planLabelClass() {
            return this.payload.plan?.can_reactivate ? 'is-ready' : 'is-blocked';
        },
        reactivateDisabled() {
            return this.reactivating
                || !this.payload.plan?.can_reactivate
                || !this.payload.permissions?.can_reactivate;
        },
        amountPaid() {
            const raw = this.header.acconto_versato;
            if (raw === null || raw === undefined || raw === '') {
                return '—';
            }
            const num = Number(String(raw).replace(',', '.'));
            if (Number.isNaN(num)) {
                return raw;
            }

            return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(num);
        },
        allocateNewHint() {
            if (this.payload.plan?.allocate_new_hint) {
                return this.payload.plan.allocate_new_hint;
            }

            if (!this.payload.plan?.can_reactivate || !this.payload.plan?.requires_confirm_new_number) {
                return null;
            }

            return `Il numero ${this.header.canceled_num} è già occupato da un altro ordine live: alla Riattiva verrà assegnato un nuovo numPrenotazione.`;
        },
    },
    methods: {
        label(key, fallback) {
            return this.payload.labels?.[key] ?? fallback;
        },
        swalConfirmed(result) {
            return result && (result.isConfirmed === true || result.value === true);
        },
        async onReactivateClick() {
            if (!this.payload.plan?.can_reactivate || !this.payload.permissions?.can_reactivate) {
                return;
            }

            const plan = this.payload.plan;
            let confirmNewNumber = false;

            if (plan.requires_confirm_new_number) {
                const hint = this.allocateNewHint || 'Continuare con un nuovo numPrenotazione?';
                const swal = await this.confirmSwal(
                    `${this.label('reactivate', 'Riattiva')} ordine ${this.header.canceled_num}?`,
                    hint,
                    'Conferma e riattiva',
                );
                if (!this.swalConfirmed(swal)) {
                    return;
                }
                confirmNewNumber = true;
            } else {
                const swal = await this.confirmSwal(
                    'Riattivare l\'ordine?',
                    'La testata verrà copiata in ordini_testata. BeeStore resta cancellato.',
                    'Riattiva',
                );
                if (!this.swalConfirmed(swal)) {
                    return;
                }
            }

            this.reactivating = true;
            try {
                const body = confirmNewNumber ? { confirm_new_number: 1 } : {};
                const response = await window.axios.post(this.payload.routes.reactivate, body);
                if (response.data?.redirect) {
                    window.location.href = response.data.redirect;

                    return;
                }
                if (window.Swal) {
                    await window.Swal.fire('OK', response.data?.message || 'Ordine riattivato.', 'success');
                }
            } catch (error) {
                const message = error.response?.data?.message
                    || Object.values(error.response?.data?.errors || {}).flat().join(' ')
                    || 'Riattiva non riuscita.';
                if (window.Swal) {
                    await window.Swal.fire('Errore', message, 'error');
                }
            } finally {
                this.reactivating = false;
            }
        },
        confirmSwal(title, text, confirmButtonText) {
            if (!window.Swal) {
                return Promise.resolve({ isConfirmed: window.confirm(`${title}\n\n${text}`) });
            }

            return window.Swal.fire({
                title,
                text,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText,
                cancelButtonText: 'Annulla',
            });
        },
    },
};
</script>
